import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { getCurrentUser, getAppSettings } from '../api/settings.api';

/**
 * Effective access role for the current user.
 *
 * - `admin`      – App Admin: full access including Settings.
 * - `regular`    – Regular user (listed in Manage Access or "All users" is on):
 *                  full access except Settings.
 * - `restricted` – Explicitly restricted: no access to any view.
 * - `no_access`  – Not listed in Manage Access and "All users" is off:
 *                  no access to any view.
 */
export type UserRole = 'admin' | 'regular' | 'restricted' | 'no_access';

export interface CurrentUserAccess {
  role: UserRole;
  isLoading: boolean;
  error: string | null;
  /** Convenience: true only for admin role */
  isAdmin: boolean;
  /** Convenience: true for admin or regular (i.e. has access to views) */
  hasAccess: boolean;
  /** Re-fetch the access data */
  refetch: () => void;
}

const defaultValue: CurrentUserAccess = {
  role: 'regular',
  isLoading: true,
  error: null,
  isAdmin: false,
  hasAccess: false,
  refetch: () => {},
};

const UserAccessContext = createContext<CurrentUserAccess>(defaultValue);

/**
 * Provider that fetches the current user's access role once and shares it
 * with all consumers via context.
 */
export function UserAccessProvider({ children }: { children: ReactNode }) {
  const [role, setRole] = useState<UserRole>('regular');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAccess = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [me, appSettings] = await Promise.all([
        getCurrentUser(),
        getAppSettings(),
      ]);

      if (me.isAdmin) {
        setRole('admin');
      } else if (me.isRestricted) {
        setRole('restricted');
      } else if (appSettings.allUsersAccessEnabled) {
        setRole('regular');
      } else {
        // "All users" is off. The user has a record (auto-created by getMe)
        // but is not admin and not restricted → they were added via Manage Access.
        setRole('regular');
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to check access';
      setError(message);
      setRole('no_access');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccess();
  }, [fetchAccess]);

  const value: CurrentUserAccess = {
    role,
    isLoading,
    error,
    isAdmin: role === 'admin',
    hasAccess: role === 'admin' || role === 'regular',
    refetch: fetchAccess,
  };

  return React.createElement(UserAccessContext.Provider, { value }, children);
}

/**
 * Consume the current user's access role. Must be used within a UserAccessProvider.
 */
export function useCurrentUserAccess(): CurrentUserAccess {
  return useContext(UserAccessContext);
}
